// src/screens/ForgotPassword.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config/env";

const API_KEY = "darwintec-solutions-X-prompt-jai";

function formatThaiPhone(digits) {
  const d = String(digits || "").replace(/[^\d]/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  return `${d.slice(0, 3)}-${d.slice(3)}`;
}

function SuccessModal({ open, title = "บันทึกข้อมูลสำเร็จ", desc = "ดำเนินการเรียบร้อยแล้ว", onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-[1px] grid place-items-center px-4"
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M20 6 9 17l-5-5"
              stroke="#059669"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="mt-4 text-center text-[18px] font-semibold text-slate-900">{title}</div>
        <div className="mt-2 text-center text-[14px] text-slate-600">{desc}</div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-[160px] rounded-xl bg-[#407865] text-white font-medium"
          >
            ตกลง
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const nav = useNavigate();

  const [step, setStep] = useState(1);

  // --- step 1 ---
  const [phoneRaw, setPhoneRaw] = useState("");
  const phoneDisplay = useMemo(() => formatThaiPhone(phoneRaw), [phoneRaw]);
  const [phoneErr, setPhoneErr] = useState("");

  // --- step 2 ---
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const phoneDigits = useMemo(
    () => phoneRaw.replace(/[^\d]/g, "").slice(0, 10),
    [phoneRaw]
  );

  const [username, setUsername] = useState("");
  const [usernameErr, setUsernameErr] = useState("");

  // ✅ เบอร์ต้อง 10 ตัวเท่านั้น
  const canSubmitPhone = useMemo(() => {
    return phoneDigits.length === 10 && String(username || "").trim().length > 0;
  }, [phoneDigits, username]);

  async function onConfirmPhone(e) {
    e?.preventDefault?.();
    setPhoneErr("");
    setUsernameErr("");

    if (String(username || "").trim().length === 0) {
      setUsernameErr("กรุณากรอก username");
      return;
    }

    if (phoneDigits.length !== 10) {
      setPhoneErr("กรุณากรอกเบอร์ให้ถูกต้อง (10 หลัก)");
      return;
    }

    setStep(2);
  }

  const canSubmitPwd = useMemo(() => {
    return (
      phoneDigits.length === 10 &&
      String(username || "").trim().length > 0 &&
      pwd.length > 0 &&
      pwd2.length > 0 &&
      pwd === pwd2
    );
  }, [phoneDigits, username, pwd, pwd2]);


  async function onConfirmNewPassword(e) {
    e?.preventDefault?.();
    setPwdErr("");

    if (String(username || "").trim().length === 0) {
      setPwdErr("กรุณากรอก username");
      setStep(1);
      return;
    }

    if (phoneDigits.length !== 10) {
      setPwdErr("กรุณากรอกเบอร์ให้ถูกต้อง (10 หลัก)");
      setStep(1);
      return;
    }

    if (!pwd || !pwd2) {
      setPwdErr("กรุณากรอกรหัสผ่านให้ครบ");
      return;
    }

    if (pwd !== pwd2) {
      setPwdErr("รหัสผ่านไม่ตรงกัน");
      return;
    }

    try {
      // ✅ POST /api/auth/forgot-password
      await axios.post(
        `${config.api || ""}/api/auth/forgot-password`,
        {
          phone_number: phoneDigits,
          username: String(username || "").trim(),
          new_password: String(pwd),
        },
        {
          headers: { "X-API-KEY": API_KEY },
          withCredentials: false,
        }
      );


      // ✅ เปิด modal ก่อน แล้วค่อยเด้งไป login
      setSuccessOpen(true);
      setTimeout(() => nav("/login", { replace: true }), 1500);

    } catch (err) {
      const msg = err?.response?.data?.message || "ไม่พบ username หรือ เบอร์นี้ติดต่อในระบบ";
      setStep(1);
      setPhoneErr(String(msg)); // ให้แสดงใต้ช่องเบอร์
    }
  }

  return (
    <div className="min-h-screen w-full bg-[#407865] flex items-center justify-center px-4 py-10">
      <SuccessModal
        open={successOpen}
        title="บันทึกข้อมูลสำเร็จ"
        desc="ระบบตั้งรหัสผ่านใหม่เรียบร้อยแล้ว"
        onClose={() => {
          setSuccessOpen(false);
          nav("/login", { replace: true });
        }}
      />
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-xl px-7 py-10">
        {step === 1 ? (
          <>
            <div className="text-center text-[22px] font-semibold text-[#111827]">ลืมรหัสผ่าน</div>

            <form onSubmit={onConfirmPhone} className="mt-7">
              <div className="text-[14px] font-medium text-[#111827]">
                กรอก username ของท่าน <span className="text-red-500">*</span>
              </div>

              <input
                className={[
                  "mt-2 w-full h-12 rounded-lg border px-4 text-[14px] outline-none",
                  usernameErr ? "border-red-500" : "border-[#9CA3AF]",
                ].join(" ")}
                placeholder="xxxxxxxxxx"
                value={username}
                onChange={(e) => {
                  setUsernameErr("");
                  setUsername(e.target.value);
                }}
              />

              {usernameErr && (
                <div className="mt-2 text-right text-[12px] text-red-500">{usernameErr}</div>
              )}

              <div className="text-[14px] font-medium text-[#111827]">
                กรอกหมายเลขโทรศัพท์ของท่าน <span className="text-red-500">*</span>
              </div>

              <input
                className={[
                  "mt-2 w-full h-12 rounded-lg border px-4 text-[14px] outline-none",
                  phoneErr ? "border-red-500" : "border-[#9CA3AF]",
                ].join(" ")}
                placeholder="0xx-xxxxxxx"
                inputMode="numeric"
                value={phoneDisplay}
                onChange={(e) => {
                  setPhoneErr("");
                  setPhoneRaw(e.target.value.replace(/[^\d]/g, "").slice(0, 10));
                }}
              />

              {phoneErr && (
                <div className="mt-2 text-right text-[12px] text-red-500">{phoneErr}</div>
              )}

              <button
                type="submit"
                disabled={!canSubmitPhone}
                className={[
                  "mt-6 w-full h-12 rounded-lg font-semibold text-white",
                  canSubmitPhone ? "bg-[#407865]" : "bg-slate-300 cursor-not-allowed",
                ].join(" ")}
              >
                ยืนยัน
              </button>

              <button
                type="button"
                onClick={() => nav(-1)}
                className="mt-6 w-full text-center text-[14px] text-[#009951] font-medium"
              >
                กลับไปหน้าก่อนหน้า
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="text-center text-[22px] font-semibold text-[#111827]">ตั้งค่ารหัสผ่านใหม่</div>

            <form onSubmit={onConfirmNewPassword} className="mt-7 space-y-5">
              <div>
                <div className="text-[14px] font-medium text-[#111827]">
                  รหัสผ่านใหม่ <span className="text-red-500">*</span>
                </div>
                <input
                  className={[
                    "mt-2 w-full h-12 rounded-lg border px-4 text-[14px] outline-none",
                    pwdErr ? "border-red-500" : "border-[#9CA3AF]",
                  ].join(" ")}
                  placeholder="ตั้งรหัสผ่านใหม่"
                  type="password"
                  value={pwd}
                  onChange={(e) => {
                    setPwdErr("");
                    setPwd(e.target.value);
                  }}
                />
              </div>

              <div>
                <div className="text-[14px] font-medium text-[#111827]">
                  กรอกรหัสผ่านใหม่อีกครั้ง <span className="text-red-500">*</span>
                </div>
                <input
                  className={[
                    "mt-2 w-full h-12 rounded-lg border px-4 text-[14px] outline-none",
                    pwdErr ? "border-red-500" : "border-[#9CA3AF]",
                  ].join(" ")}
                  placeholder="กรอกรหัสผ่านใหม่"
                  type="password"
                  value={pwd2}
                  onChange={(e) => {
                    setPwdErr("");
                    setPwd2(e.target.value);
                  }}
                />

                {pwdErr && (
                  <div className="mt-2 text-right text-[12px] text-red-500">{pwdErr}</div>
                )}
              </div>

              <button
                type="submit"
                disabled={!canSubmitPwd}
                className={[
                  "w-full h-12 rounded-lg font-semibold text-white",
                  canSubmitPwd ? "bg-[#407865]" : "bg-slate-300 cursor-not-allowed",
                ].join(" ")}
              >
                ยืนยันตั้งค่ารหัสผ่านใหม่
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-[14px] text-[#009951] font-medium"
              >
                กลับไปแก้เบอร์โทร
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
